import React from 'react'
import { Box, Tooltip } from '@mui/material'

import Txt from './Txt'
import { RestaurantTourColumn, TourTarget, useRestaurantTour } from '../utils/settlement'

// Dieselben Grüntöne wie Seite 2 des Briefs (buildMarketingPdf).
const GREEN_DEEP = '#2b8a4f'
const GREEN_LIGHT = '#edf7f1'
const BRAND = '#7a2350'

const formatEuro = (value: number): string =>
	`${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

const formatPeople = (value: number): string => Math.round(value).toLocaleString('de-DE')

const formatNegativeEuro = (value: number): string => (value ? `− ${formatEuro(value)}` : '')

// Die Rechnung des Restaurants, Zeile für Zeile wie im Brief.
const TOUR_ROWS: {
	label: string
	value: (tour: RestaurantTourColumn) => string
	bold?: boolean
}[] = [
	{ label: 'Mindestbestellwert', value: (tour) => formatEuro(tour.orderValue) },
	{
		label: 'Liefergebühr',
		value: (tour) => (tour.deliveryFee ? `+ ${formatEuro(tour.deliveryFee)}` : ''),
	},
	{ label: 'Wareneinsatz (30 %)', value: (tour) => formatNegativeEuro(tour.foodCost) },
	{ label: 'Provision (14 %)', value: (tour) => formatNegativeEuro(tour.commission) },
	{ label: 'Zahlungsgebühren', value: (tour) => formatNegativeEuro(tour.payment) },
	{
		label: 'Fahrtkosten (Fahrzeug und Fahrer)',
		value: (tour) => formatNegativeEuro(tour.driveCost),
	},
	{ label: 'Tourdauer', value: (tour) => `${Math.round(tour.minutes)} min` },
	{ label: 'Ihnen bleiben je Tour', value: (tour) => formatEuro(tour.profit), bold: true },
]

const sxTable = {
	display: 'grid',
	gridTemplateColumns: '1fr repeat(3, 6.5rem)',
	m: '0.25rem 0 0.75rem 0',
} as const

const sxTableFour = { ...sxTable, gridTemplateColumns: '1fr repeat(4, 6.5rem)' } as const

const sxToggleRow = { display: 'flex', justifyContent: 'flex-end', m: '0.75rem 0 0 0' } as const

const sxToggle = {
	'cursor': 'pointer',
	'&:hover': { textDecoration: 'underline' },
} as const

const sxCell = {
	textAlign: 'right',
	p: '0.3rem 0.5rem',
	borderBottom: '1px solid',
	borderColor: 'divider',
} as const

const sxGroupCell = { ...sxCell, bgcolor: GREEN_LIGHT } as const

const sxLabelCell = { ...sxCell, textAlign: 'left' } as const

// Die Sicht des Haushalts steht abgesetzt unter der Rechnung des Restaurants, ohne Linien.
const sxHouseholdCell = { textAlign: 'right', p: '0.3rem 0.5rem' } as const
const sxHouseholdGroupCell = { ...sxHouseholdCell, bgcolor: GREEN_LIGHT } as const
const sxHouseholdLabelCell = { ...sxHouseholdCell, textAlign: 'left' } as const

const slotPropsTooltip = {
	tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: '28rem' } },
} as const

const sxHelp = { cursor: 'help' } as const

const sxSeparator = {
	gridColumn: '1 / -1',
	borderTop: '1px solid',
	borderColor: 'text.secondary',
	m: '0.75rem 0 0 0',
} as const

// Kilometer und Minuten stehen im Bild auf der Strecke (getRouteMapImage); der Abstand oben hält
// das Leitfaden-Icon der Tourkarte vom Bild frei.
const sxMapFrame = {
	width: '100%',
	maxWidth: '32rem',
	m: '1.5rem auto 0.25rem',
} as const

const sxMap = { display: 'block', width: '100%' } as const

const sxLegend = {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '0.4rem',
	flexWrap: 'wrap',
} as const

const sxAreaSwatch = {
	width: '0.6rem',
	height: '0.6rem',
	borderRadius: '2px',
	border: '1px solid',
	borderColor: GREEN_DEEP,
	bgcolor: GREEN_LIGHT,
} as const

const sxRouteSwatch = {
	width: '1rem',
	height: '3px',
	borderRadius: '2px',
	bgcolor: BRAND,
	m: '0 0 0 0.5rem',
} as const

const PERSON_WIDTH = 8
const PERSON_HEIGHT = 12

// Personen-Figuren wie im Brief: so viele Personen bräuchte ein Haushalt für seinen Anteil.
const PersonIcons = React.memo(
	({ count, color }: { count: number; color: string }): JSX.Element => (
		<svg
			width={count * PERSON_WIDTH}
			height={PERSON_HEIGHT}
			viewBox={`0 0 ${count * PERSON_WIDTH} ${PERSON_HEIGHT}`}
			aria-label={`${count} Personen`}
		>
			{Array.from({ length: count }, (_, index) => index * PERSON_WIDTH).map((left) => (
				<g key={left} fill={color}>
					<circle cx={left + 3.5} cy={2.5} r={2.3} />
					<path d={`M${left + 0.5} 12 V8.8 A3 3 0 0 1 ${left + 6.5} 8.8 V12 Z`} />
				</g>
			))}
		</svg>
	)
)
PersonIcons.displayName = 'PersonIcons'

// Die Tourrechnung eines Restaurants in einen Ort, auch wenn es dort noch nicht liefert.
// hideMap: im Dashboard steht die Strecke schon auf der großen Karte.
export const RestaurantTourContent = ({
	target,
	hideMap,
}: {
	target: TourTarget
	hideMap?: boolean
}): JSX.Element => {
	const { tour, loading } = useRestaurantTour(target)

	// Die API rechnet bis 4 Haushalte; die 4er-Spalte kommt erst auf Knopfdruck dazu.
	const [showFourHouseholds, setShowFourHouseholds] = React.useState(false)
	const handleToggleFourHouseholds = React.useCallback(
		() => setShowFourHouseholds((previous) => !previous),
		[]
	)
	const columns = React.useMemo(
		() => (tour?.tours ?? []).filter(({ households }) => showFourHouseholds || households <= 3),
		[showFourHouseholds, tour?.tours]
	)

	const householdSizeTooltip = React.useMemo(() => {
		const shares = tour?.householdSizeShares ?? []
		const largestPersons = shares[shares.length - 1]?.persons
		// Wie die API (getAverageHouseholdSize): die größte Klasse zählt mit ihrer Untergrenze.
		const averageHouseholdSize = shares.reduce((sum, { persons, share }) => sum + persons * share, 0)
		return [
			'Genau so viele Personen:',
			...shares.map(
				({ persons, share }) =>
					`${persons === largestPersons ? `${persons}+` : persons} Pers.: ${Math.round(
						share * 100
					)} % der Haushalte · ${Math.round(
						((persons * share) / averageHouseholdSize) * 100
					)} % der Einwohner`
			),
			'',
			'So viele Personen oder mehr:',
			...shares.map(
				({ persons }) =>
					`${persons}+ Pers.: ${Math.round(
						shares
							.filter((size) => size.persons >= persons)
							.reduce((sum, size) => sum + size.share, 0) * 100
					)} % der Haushalte · ${Math.round(
						(shares
							.filter((size) => size.persons >= persons)
							.reduce((sum, size) => sum + size.persons * size.share, 0) /
							averageHouseholdSize) *
							100
					)} % der Einwohner`
			),
		].join('\n')
	}, [tour?.householdSizeShares])

	return loading ? (
		<Txt variant="body2">Strecke wird berechnet …</Txt>
	) : !tour?.success ? (
		<Txt variant="body2" color="error.main">
			{tour?.error ?? 'Tour nicht berechenbar'}
		</Txt>
	) : (
		<Box width={showFourHouseholds ? '41rem' : '34rem'} maxWidth="100%">
			{tour.mapImage && !hideMap ? (
				<>
					<Box sx={sxMapFrame}>
						<Box component="img" src={tour.mapImage} alt="Strecke und Liefergebiet" sx={sxMap} />
					</Box>
					<Box sx={sxLegend}>
						<Box sx={sxAreaSwatch} />
						<Txt variant="caption" color="text.secondary">
							Liefergebiet: {tour.deliveryAreaMinutes} Minuten Fahrtzeit um das Zentrum von{' '}
							{tour.settlementName}
						</Txt>
						<Box sx={sxRouteSwatch} />
						<Txt variant="caption" color="text.secondary">
							Strecke
						</Txt>
					</Box>
				</>
			) : null}
			{tour.assumedMinOrderValue ? (
				<Txt variant="caption" color="text.secondary" display="block" m="0.5rem 0 0 0">
					{tour.restaurantName} liefert noch nicht nach {tour.settlementName}: der MBW ist auf 30 %
					Deckungsbeitrag hochgerechnet.
				</Txt>
			) : null}
			{tour.tours.length > 3 ? (
				<Box sx={sxToggleRow}>
					<Box component="span" sx={sxToggle} onClick={handleToggleFourHouseholds}>
						<Txt variant="caption" color={GREEN_DEEP}>
							{showFourHouseholds ? '4 Haushalte ausblenden' : '+ 4 Haushalte'}
						</Txt>
					</Box>
				</Box>
			) : null}
			<Box sx={columns.length > 3 ? sxTableFour : sxTable}>
				<Box sx={sxLabelCell} />
				{columns.map((column) => (
					<Box key={column.households} sx={column.isSingleOrder ? sxCell : sxGroupCell}>
						<Txt
							variant="body2"
							fontWeight="bold"
							color={column.isSingleOrder ? 'text.primary' : GREEN_DEEP}
						>
							{column.isSingleOrder ? 'Heute' : 'Mitbestellt'}
						</Txt>
						<Txt variant="caption" color="text.secondary">
							{column.households} {column.households === 1 ? 'Haushalt' : 'Haushalte'}
						</Txt>
					</Box>
				))}
				{TOUR_ROWS.map((row) => (
					<React.Fragment key={row.label}>
						<Box sx={sxLabelCell}>
							<Txt
								variant="body2"
								fontWeight={row.bold ? 'bold' : undefined}
								color={row.bold ? 'text.primary' : 'text.secondary'}
							>
								{row.label}
							</Txt>
						</Box>
						{columns.map((column) => (
							<Box key={column.households} sx={column.isSingleOrder ? sxCell : sxGroupCell}>
								<Txt
									variant="body2"
									fontWeight={row.bold ? 'bold' : undefined}
									color={row.bold && !column.isSingleOrder ? GREEN_DEEP : 'text.primary'}
								>
									{row.value(column)}
								</Txt>
							</Box>
						))}
					</React.Fragment>
				))}
				<Box sx={sxSeparator} />
				<Box sx={sxHouseholdLabelCell}>
					<Txt variant="body2" color="text.secondary">
						Ein Haushalt zahlt
					</Txt>
				</Box>
				{columns.map((column) => (
					<Box
						key={column.households}
						sx={column.isSingleOrder ? sxHouseholdCell : sxHouseholdGroupCell}
					>
						<Txt variant="body2">{formatEuro(column.householdCost)}</Txt>
					</Box>
				))}
				<Box sx={sxHouseholdLabelCell}>
					<Txt variant="body2" color="text.secondary">
						benötigte Personen im Haushalt
					</Txt>
				</Box>
				{columns.map((column) => (
					<Box
						key={column.households}
						sx={column.isSingleOrder ? sxHouseholdCell : sxHouseholdGroupCell}
					>
						<PersonIcons
							count={column.personCount}
							color={column.isSingleOrder ? 'currentColor' : GREEN_DEEP}
						/>
					</Box>
				))}
				<Box sx={sxHouseholdLabelCell}>
					<Tooltip title={householdSizeTooltip} slotProps={slotPropsTooltip} arrow>
						<Box sx={sxHelp}>
							<Txt variant="body2" color="text.secondary">
								Haushalte mit so vielen Personen oder mehr
							</Txt>
						</Box>
					</Tooltip>
				</Box>
				{columns.map((column) => (
					<Box
						key={column.households}
						sx={column.isSingleOrder ? sxHouseholdCell : sxHouseholdGroupCell}
					>
						<Txt variant="body2" color={column.isSingleOrder ? 'text.primary' : GREEN_DEEP}>
							{column.householdShareIsUpperBound ? 'unter ' : ''}
							{Math.round(column.householdShare * 100)} %
						</Txt>
					</Box>
				))}
				{tour.settlementInhabitants ? (
					<>
						<Box sx={sxHouseholdLabelCell}>
							<Txt
								variant="body2"
								color="text.secondary"
								title={`Von ${formatPeople(tour.settlementInhabitants)} Einwohnern im ${
									tour.deliveryAreaMinutes
								}-Minuten-Liefergebiet von ${
									tour.settlementName
								} leben so viele in Haushalten, die ihren Anteil allein erreichen.`}
							>
								erreichbare Menschen in {tour.deliveryAreaMinutes} min
							</Txt>
						</Box>
						{columns.map((column) => (
							<Box
								key={column.households}
								sx={column.isSingleOrder ? sxHouseholdCell : sxHouseholdGroupCell}
							>
								<Txt variant="body2" color={column.isSingleOrder ? 'text.primary' : GREEN_DEEP}>
									{column.householdShareIsUpperBound ? 'unter ' : ''}
									{formatPeople(column.personShare * (tour.settlementInhabitants as number))}
								</Txt>
							</Box>
						))}
						{tour.orderRate !== null && tour.orderValuePerPerson !== null ? (
							<>
								<Box sx={sxHouseholdLabelCell}>
									<Txt
										variant="body2"
										color="text.secondary"
										title={`Erreichbare Menschen × ${Math.round(
											tour.orderRate * 100
										)} % Bestellquote (Anteil, der im Monat bestellt, nach Altersstruktur) × ${formatEuro(
											tour.orderValuePerPerson
										)} Bestellwert je Person`}
									>
										Umsatz/Monat ({Math.round(tour.orderRate * 100)} % Bestellquote)
									</Txt>
								</Box>
								{columns.map((column) => (
									<Box
										key={column.households}
										sx={column.isSingleOrder ? sxHouseholdCell : sxHouseholdGroupCell}
									>
										<Txt
											variant="body2"
											fontWeight="bold"
											color={column.isSingleOrder ? 'text.primary' : GREEN_DEEP}
										>
											{column.householdShareIsUpperBound ? 'unter ' : ''}
											{formatPeople(
												column.personShare *
													(tour.settlementInhabitants as number) *
													(tour.orderRate as number) *
													(tour.orderValuePerPerson as number)
											)}{' '}
											€
										</Txt>
									</Box>
								))}
							</>
						) : null}
					</>
				) : null}
			</Box>
		</Box>
	)
}
